import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/KpiCard";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { OP_SECTIONS, ADMIN_SECTIONS } from "@/lib/sections";

const listFor = (role) => (role === "office" ? ADMIN_SECTIONS : OP_SECTIONS);
const Sections = ({ value, onChange, role }) => {
  const list = listFor(role);
  const ALL = list.map(([id]) => id);
  const sel = value ?? ALL;
  const toggle = (id, on) => onChange(on ? ALL.filter((x) => sel.includes(x) || x === id) : sel.filter((x) => x !== id));
  return (
    <div className="space-y-2" data-testid="user-sections">
      <div className="flex items-center justify-between"><Label>Schede visibili</Label>
        <div className="flex gap-2 text-xs"><button type="button" className="text-blue-600 hover:underline" onClick={() => onChange(ALL)} data-testid="user-sections-all">Tutte</button><button type="button" className="text-slate-500 hover:underline" onClick={() => onChange([])} data-testid="user-sections-none">Nessuna</button></div>
      </div>
      <div className="grid grid-cols-1 gap-2 rounded-lg border p-3 sm:grid-cols-2">
        {list.map(([id, label]) => (
          <label key={id} className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={sel.includes(id)} onCheckedChange={(c) => toggle(id, !!c)} data-testid={`user-section-${id}`} />{label}
          </label>
        ))}
      </div>
    </div>
  );
};
const sectionsLabel = (u) => (!["operator", "office"].includes(u.role) || !Array.isArray(u.sections) ? "Tutte" : `${u.sections.length} di ${listFor(u.role).length}`);

const blank = { email: "", name: "", password: "", role: "operator", station_id: "", active: true, sections: null };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [stations, setStations] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => { api.get("/users").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); api.get("/stations").then((r) => setStations(r.data)); }, [load]);
  const stName = (id) => stations.find((s) => s.id === id)?.name || "—";

  const save = async () => {
    const payload = { ...edit, station_id: edit.role === "operator" ? edit.station_id || null : null, password: edit.password || null };
    try {
      if (edit.id) {
        const { data } = await api.put(`/users/${edit.id}`, payload);
        if (data.new_token) localStorage.setItem("fleet_token", data.new_token);
      } else await api.post("/users", payload);
      toast.success("Utente salvato");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Utenti" subtitle="Gestisci operatori di stazione e amministratori.">
        <Button onClick={() => setEdit(blank)} data-testid="add-user-button"><Plus className="mr-2 h-4 w-4" /> Nuovo utente</Button>
      </PageHeader>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Ruolo</th><th className="px-4 py-3">Stazione</th><th className="px-4 py-3">Schede</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${u.role === "admin" ? "border-slate-300 bg-slate-900 text-white" : u.role === "supervisor" ? "border-amber-200 bg-amber-50 text-amber-800" : u.role === "office" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-blue-200 bg-blue-50 text-blue-700"}`}>{u.role === "admin" ? "Admin" : u.role === "supervisor" ? "Supervisore" : u.role === "office" ? "Amministrativo" : "Operatore"}</span></td>
                  <td className="px-4 py-3">{u.role === "operator" ? stName(u.station_id) : "Tutte"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-testid={`user-sections-${u.id}`}>{sectionsLabel(u)}</td>
                  <td className="px-4 py-3 text-xs">{u.active !== false ? <span className="text-emerald-600">Attivo</span> : <span className="text-slate-400">Disattivato</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEdit({ ...blank, ...u, password: "", station_id: u.station_id || "" })} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                    {u.id !== me.id && <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nome</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="user-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="user-email-input" /></div>
              <div className="space-y-1.5"><Label>{edit.id ? "Nuova password (lascia vuoto per non cambiarla)" : "Password"}</Label><Input type="text" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} data-testid="user-password-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Ruolo</Label>
                  <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v, sections: null })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="operator">Operatore</SelectItem>{!me.office && <SelectItem value="admin">Admin</SelectItem>}{!me.office && <SelectItem value="office">Amministrativo (senza registro operazioni)</SelectItem>}<SelectItem value="supervisor">Supervisore (sola visione)</SelectItem></SelectContent>
                  </Select>
                </div>
                {edit.role === "operator" && (
                  <div className="space-y-1.5"><Label>Stazione</Label>
                    <Select value={edit.station_id} onValueChange={(v) => setEdit({ ...edit, station_id: v })}>
                      <SelectTrigger data-testid="user-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {["operator", "office"].includes(edit.role) && <Sections key={edit.role} role={edit.role} value={edit.sections} onChange={(v) => setEdit({ ...edit, sections: v })} />}
              <div className="flex items-center gap-2"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="user-active-switch" /><Label>Account attivo</Label></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="user-save-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
